import { useState } from 'react'
import { useStorageUsage, useStoragePlans, usePurchaseStorage } from '@/api/hooks/useStorage'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { HardDrive, ShoppingCart, Loader2, ExternalLink, X, Infinity } from 'lucide-react'
import { toast } from 'sonner'
import { useLanguage } from '@/i18n/LanguageContext'

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const gb = bytes / (1024 * 1024 * 1024)
  if (gb >= 1) return `${gb.toFixed(1)} GB`
  const mb = bytes / (1024 * 1024)
  return `${mb.toFixed(0)} MB`
}

export function StorageBar() {
  const { t } = useLanguage()
  const { data: usage } = useStorageUsage()
  const [showPlans, setShowPlans] = useState(false)
  const { data: plans, refetch: fetchPlans } = useStoragePlans()
  const purchaseMutation = usePurchaseStorage()

  if (!usage) return null

  if (usage.isAdmin) {
    return (
      <div className="flex items-center gap-3 rounded-2xl bg-gradient-to-r from-primary/5 via-card to-card border border-primary/15 px-5 py-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10">
          <HardDrive className="h-4.5 w-4.5 text-primary" />
        </div>
        <span className="text-sm font-semibold tabular-nums">{formatBytes(usage.usedBytes)}</span>
        <span className="text-xs text-muted-foreground">{t('usado')}</span>
        <Badge className="ml-auto border-primary/20 bg-primary/10 text-primary text-[10px] font-bold gap-1 px-2.5 py-0.5">
          <Infinity className="h-3 w-3" /> {t('Ilimitado')}
        </Badge>
      </div>
    )
  }

  const pct = usage.percentUsed
  const remaining = usage.quotaBytes! - usage.usedBytes
  const barGradient = pct >= 90
    ? 'from-red-500 to-red-400'
    : pct >= 75
      ? 'from-yellow-500 to-amber-400'
      : 'from-primary to-emerald-400'
  const accentColor = pct >= 90 ? 'text-red-500' : pct >= 75 ? 'text-yellow-500' : 'text-primary'

  const handleOpenPlans = () => {
    setShowPlans(!showPlans)
    if (!showPlans && !plans) fetchPlans()
  }

  const handlePurchase = async (planId: string) => {
    try {
      const result = await purchaseMutation.mutateAsync(planId)
      if (result.billingUrl) {
        window.open(result.billingUrl, '_blank')
        toast.success(t('Cobranca gerada! Complete o pagamento via PIX.'))
      }
    } catch {
      toast.error(t('Erro ao gerar cobranca'))
    }
  }

  return (
    <div className="space-y-3">
      <div className="relative overflow-hidden rounded-2xl border border-border/40 bg-card/80 backdrop-blur-sm shadow-sm">
        <div className="flex items-center gap-4 px-5 py-3.5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5">
            <HardDrive className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-baseline gap-1.5">
                <span className="text-sm font-bold tabular-nums">{formatBytes(usage.usedBytes)}</span>
                <span className="text-xs text-muted-foreground">{t('de')} {formatBytes(usage.quotaBytes!)}</span>
              </div>
              <span className={`text-xs font-semibold ${accentColor}`}>
                {formatBytes(remaining)} {t('livres')}
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-muted/80 overflow-hidden">
              <div
                className={`h-full rounded-full bg-gradient-to-r ${barGradient} storage-bar-animate`}
                style={{ '--bar-pct': Math.min(pct, 100) / 100 } as React.CSSProperties}
              />
            </div>
          </div>

          {pct >= 80 && (
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 gap-1.5 rounded-xl border-primary/30 text-primary hover:bg-primary/10 hover:text-primary transition-colors"
              onClick={handleOpenPlans}
            >
              <ShoppingCart className="h-3.5 w-3.5" />
              {t('Comprar')}
            </Button>
          )}
        </div>
      </div>

      {showPlans && (
        <div className="rounded-2xl border border-border/40 bg-card/90 backdrop-blur-sm p-5 shadow-lg slide-up">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold">{t('Comprar mais espaco')}</h3>
              <p className="text-xs text-muted-foreground mt-0.5">{t('O espaco e adicionado automaticamente apos o pagamento')}</p>
            </div>
            <button
              onClick={() => setShowPlans(false)}
              className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {!plans ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {plans.map((plan) => (
                <button
                  key={plan.id}
                  onClick={() => handlePurchase(plan.id)}
                  disabled={purchaseMutation.isPending}
                  className="group flex flex-col items-center gap-2 rounded-xl border border-border/40 bg-muted/20 p-4 transition-all duration-200 hover:border-primary/40 hover:bg-primary/5 hover:shadow-md hover:-translate-y-0.5 disabled:opacity-50"
                >
                  <span className="text-xl font-extrabold tracking-tight">{plan.label}</span>
                  <span className="text-sm text-muted-foreground font-medium">
                    R$ {plan.price.toFixed(2).replace('.', ',')}
                  </span>
                  <div className="flex items-center gap-1 text-xs text-primary font-semibold opacity-70 group-hover:opacity-100 transition-opacity">
                    <ExternalLink className="h-3 w-3" /> PIX
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
